import { createClient } from '../../client/createClient'
import { createSubscan } from '../../explorer'
import { createPolkadotAssetId } from '../../utils'
import type { Chain } from '../types'

type CreateMantaChainParameters = {
  explorerApiKey?: string
  explorerProxyUrl?: string
  rpcEndpoints?: string[]
}

export const createMantaChain = ({
  explorerApiKey,
  explorerProxyUrl,
  rpcEndpoints = mantaWebsocketRpcUrls,
}: CreateMantaChainParameters): Chain => {
  return {
    /** {@link https://polkadot.subscan.io/parachain_list?status=parachain} */
    id: 2104,
    /** {@link https://github.com/paritytech/ss58-registry/blob/main/ss58-registry.json} */
    ss58Prefix: 77,
    network: 'manta-atlantic',
    name: 'Manta Atlantic',
    blockTimeSeconds: 12,
    nativeToken: {
      name: 'Manta Network',
      symbol: 'MANTA',
      decimals: 18,
      currency: {
        symbol: 'MANTA',
        type: 'CryptoCurrency',
        id: 'manta-network',
        name: 'Manta Network',
        updatedAt: 0,
      },
    },
    client: createClient(rpcEndpoints),
    explorer: createSubscan({
      name: 'Subscan',
      baseUrl: 'https://manta.subscan.io',
      networkName: 'manta',
      apiKey: explorerApiKey,
      proxyUrl: explorerProxyUrl,
    }),
    rpcEndpoints,
    assetType: 'assets',
    /** {@link https://manta.subscan.io/asset_token?page=1&order=desc&order_field=holders} */
    assetMap: new Map([
      [
        8n,
        {
          asset: {
            symbol: 'DOT',
            type: 'CryptoCurrency',
            id: 'polkadot',
            name: 'Polkadot',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        10n,
        {
          asset: {
            symbol: 'GLMR',
            type: 'CryptoCurrency',
            id: 'moonbeam',
            name: 'Moonbeam',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        36n,
        {
          asset: {
            symbol: 'LP-DOT-MANTA',
            type: 'CryptoCurrency',
            id: createPolkadotAssetId({ chainId: 2104, assetId: 36n }),
            name: 'LP-DOT-MANTA',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        1000000000n,
        {
          asset: {
            symbol: 'MANDEX',
            type: 'CryptoCurrency',
            id: createPolkadotAssetId({ chainId: 2104, assetId: 1000000000n }),
            name: 'MANDEX',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        9n,
        {
          asset: {
            symbol: 'USDT',
            type: 'CryptoCurrency',
            id: 'tether',
            name: 'Tether',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        37n,
        {
          asset: {
            symbol: 'LP-GLMR-MANTA',
            type: 'CryptoCurrency',
            id: createPolkadotAssetId({ chainId: 2104, assetId: 37n }),
            name: 'LP-GLMR-MANTA',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        38n,
        {
          asset: {
            symbol: 'LP-USDT-MANTA',
            type: 'CryptoCurrency',
            id: createPolkadotAssetId({ chainId: 2104, assetId: 38n }),
            name: 'LP-USDT-MANTA',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        14n,
        {
          asset: {
            symbol: 'BUSD',
            type: 'CryptoCurrency',
            id: 'binance-peg-busd',
            name: 'BUSD',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        11n,
        {
          asset: {
            symbol: 'ACA',
            type: 'CryptoCurrency',
            id: 'acala',
            name: 'Acala',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
      [
        31n,
        {
          asset: {
            symbol: 'DAI',
            type: 'CryptoCurrency',
            id: 'dai',
            name: 'Dai',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        25n,
        {
          asset: {
            symbol: 'POL',
            type: 'CryptoCurrency',
            id: 'polygon-ecosystem-token',
            name: 'POL',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        19n,
        {
          asset: {
            symbol: 'APE',
            type: 'CryptoCurrency',
            id: 'apecoin',
            name: 'ApeCoin',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        23n,
        {
          asset: {
            symbol: 'DAI',
            type: 'CryptoCurrency',
            id: 'dai',
            name: 'Dai',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        24n,
        {
          asset: {
            symbol: 'USDC',
            type: 'CryptoCurrency',
            id: 'usd-coin',
            name: 'USDC',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        26n,
        {
          asset: {
            symbol: 'WBTC',
            type: 'CryptoCurrency',
            id: 'wrapped-bitcoin',
            name: 'Wrapped Bitcoin',
            updatedAt: 0,
          },
          decimals: 8,
        },
      ],
      [
        27n,
        {
          asset: {
            symbol: 'ETH',
            type: 'CryptoCurrency',
            id: 'ethereum',
            name: 'Ethereum',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        34n,
        {
          asset: {
            symbol: 'tBTC',
            type: 'CryptoCurrency',
            id: 'tbtc',
            name: 'tBTC',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        33n,
        {
          asset: {
            symbol: 'USDC',
            type: 'CryptoCurrency',
            id: 'usd-coin',
            name: 'USDC',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        12n,
        {
          asset: {
            symbol: 'LDOT',
            type: 'CryptoCurrency',
            id: 'liquid-staking-dot',
            name: 'Liquid Staking DOT',
            updatedAt: 0,
          },
          decimals: 10,
        },
      ],
      [
        32n,
        {
          asset: {
            symbol: 'ETH',
            type: 'CryptoCurrency',
            id: 'ethereum',
            name: 'Ethereum',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        35n,
        {
          asset: {
            symbol: 'BNB',
            type: 'CryptoCurrency',
            id: 'binancecoin',
            name: 'BNB',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        13n,
        {
          asset: {
            symbol: 'ARB',
            type: 'CryptoCurrency',
            id: 'arbitrum',
            name: 'Arbitrum',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        17n,
        {
          asset: {
            symbol: 'UNI',
            type: 'CryptoCurrency',
            id: 'uniswap',
            name: 'Uniswap',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        20n,
        {
          asset: {
            symbol: 'ARB',
            type: 'CryptoCurrency',
            id: 'arbitrum',
            name: 'Arbitrum',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        21n,
        {
          asset: {
            symbol: 'BUSD',
            type: 'CryptoCurrency',
            id: 'binance-peg-busd',
            name: 'BUSD',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        22n,
        {
          asset: {
            symbol: 'USDT',
            type: 'CryptoCurrency',
            id: 'tether',
            name: 'Tether',
            updatedAt: 0,
          },
          decimals: 6,
        },
      ],
      [
        18n,
        {
          asset: {
            symbol: 'LINK',
            type: 'CryptoCurrency',
            id: 'chainlink',
            name: 'Chainlink',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        16n,
        {
          asset: {
            symbol: 'SHIB',
            type: 'CryptoCurrency',
            id: 'shiba-inu',
            name: 'Shiba Inu',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        28n,
        {
          asset: {
            symbol: 'POL',
            type: 'CryptoCurrency',
            id: 'polygon-ecosystem-token',
            name: 'POL',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        29n,
        {
          asset: {
            symbol: 'BNB',
            type: 'CryptoCurrency',
            id: 'binancecoin',
            name: 'BNB',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        15n,
        {
          asset: {
            symbol: 'LDO',
            type: 'CryptoCurrency',
            id: 'lido-dao',
            name: 'Lido DAO',
            updatedAt: 0,
          },
          decimals: 18,
        },
      ],
      [
        30n,
        {
          asset: {
            symbol: 'BNC',
            type: 'CryptoCurrency',
            id: 'bifrost-native-coin',
            name: 'Bifrost Native Coin',
            updatedAt: 0,
          },
          decimals: 12,
        },
      ],
    ]),
  }
}

export const mantaWebsocketRpcUrls = ['wss://ws.manta.systems']
